import React from "react";

const FIELD_CLASS =
  "mt-2 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)]";

const LABEL_CLASS =
  "block text-sm font-semibold text-[var(--do-ink)]";

export const CONTACT_FORM_LIMITS = {
  company: 100,
  firstName: 20,
  lastName: 20,
  email: 100,
  phone: 20,
  position: 20,
  address: 100,
  postalCode: 32,
  city: 180,
};

const REQUIRED_FIELDS = [
  "company",
  "firstName",
  "lastName",
  "email",
  "phone",
  "address",
];

const FIELD_LABELS = {
  company: "Société",
  firstName: "Prénom",
  lastName: "Nom",
  email: "Email",
  phone: "Téléphone",
  position: "Fonction",
  address: "Adresse",
  postalCode: "Code postal",
  city: "Ville",
};

export function getContactFormValidationError(
  value = {},
) {
  if (
    !["prospect", "client"].includes(
      value.lifecycleStage,
    )
  ) {
    return "Le statut est obligatoire.";
  }

  for (const field of REQUIRED_FIELDS) {
    if (!String(value[field] || "").trim()) {
      return "Tous les champs sont obligatoires sauf Fonction.";
    }
  }

  for (
    const [field, maxLength]
    of Object.entries(CONTACT_FORM_LIMITS)
  ) {
    if (
      String(value[field] || "").length
      > maxLength
    ) {
      return `${
        FIELD_LABELS[field] || "Champ"
      } : ${maxLength} caractères maximum.`;
    }
  }

  return "";
}

export function isContactFormValid(value) {
  return !getContactFormValidationError(value);
}

function ContactField({
  label,
  value,
  onChange,
  onBlur,
  type = "text",
  autoComplete,
  maxLength,
  required = true,
  testId,
}) {
  return (
    <label className={LABEL_CLASS}>
      <span>{label}</span>

      <input
        type={type}
        value={value || ""}
        onChange={(event) =>
          onChange(event.target.value)
        }
        onBlur={onBlur}
        autoComplete={autoComplete}
        maxLength={maxLength}
        required={required}
        data-testid={testId}
        className={FIELD_CLASS}
      />
    </label>
  );
}

export default function ContactFormFields({
  value,
  onChange,
  onAddressBlur,
  showLifecycleStage = true,
}) {
  const change = (field) => (nextValue) => {
    const next = {
      ...value,
      [field]: nextValue,
    };

    if (
      ["address", "postalCode", "city"].includes(
        field,
      ) &&
      nextValue !== value[field]
    ) {
      next.lat = null;
      next.lng = null;
    }

    onChange(next);
  };

  return (
    <div className="grid gap-3">
      {showLifecycleStage ? (
        <label className={LABEL_CLASS}>
          <span>Statut</span>

          <select
            value={
              value.lifecycleStage || "prospect"
            }
            onChange={(event) =>
              change("lifecycleStage")(
                event.target.value,
              )
            }
            required
            className={FIELD_CLASS}
          >
            <option value="prospect">
              Prospect
            </option>

            <option value="client">
              Client
            </option>
          </select>
        </label>
      ) : null}

      <ContactField
        label="Société"
        value={value.company}
        onChange={change("company")}
        maxLength={CONTACT_FORM_LIMITS.company}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <ContactField
          label="Prénom"
          value={value.firstName}
          onChange={change("firstName")}
          maxLength={CONTACT_FORM_LIMITS.firstName}
        />

        <ContactField
          label="Nom"
          value={value.lastName}
          onChange={change("lastName")}
          maxLength={CONTACT_FORM_LIMITS.lastName}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <ContactField
          label="Email"
          type="email"
          value={value.email}
          onChange={change("email")}
          autoComplete="email"
          maxLength={CONTACT_FORM_LIMITS.email}
        />

        <ContactField
          label="Téléphone"
          value={value.phone}
          onChange={change("phone")}
          autoComplete="tel"
          maxLength={CONTACT_FORM_LIMITS.phone}
        />
      </div>

      <ContactField
        label="Fonction"
        value={value.position}
        onChange={change("position")}
        maxLength={CONTACT_FORM_LIMITS.position}
        required={false}
      />

      <ContactField
        label="Adresse"
        value={value.address}
        onChange={change("address")}
        onBlur={onAddressBlur}
        autoComplete="street-address"
        maxLength={CONTACT_FORM_LIMITS.address}
        testId="contact-address-input"
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <ContactField
          label="Code postal"
          value={value.postalCode}
          onChange={change("postalCode")}
          autoComplete="postal-code"
          maxLength={CONTACT_FORM_LIMITS.postalCode}
          required={false}
          testId="contact-postal-code-input"
        />

        <ContactField
          label="Ville"
          value={value.city}
          onChange={change("city")}
          autoComplete="address-level2"
          maxLength={CONTACT_FORM_LIMITS.city}
          required={false}
          testId="contact-city-input"
        />
      </div>
    </div>
  );
}
